import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@xanectech/ui/components';

import { ShieldAlert, ArrowLeft, Home } from 'lucide-react';

interface PermissionDeniedPageProps {
  currentRole?: string | null;
  message?: string;
  hideHome?: boolean;
  onBack?: () => void | Promise<void>;
}

export const PermissionDeniedPage: React.FC<PermissionDeniedPageProps> = ({
  currentRole,
  message = "You don't have permission to access this content.",
  hideHome = false,
  onBack,
}) => {
  const navigate = useNavigate();

  const handleGoBack = async () => {
    if (onBack) {
      await onBack();
      return;
    }

    void navigate(-1);
  };

  const handleGoHome = () => {
    void navigate('/');
  };

  return (
    <div className='min-h-screen bg-background flex items-center justify-center p-4'>
      <Card className='w-full max-w-md border-destructive/20'>
        <CardHeader className='text-center pb-3'>
          <div className='mx-auto mb-4 h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center'>
            <ShieldAlert className='h-8 w-8 text-destructive' />
          </div>
          <CardTitle className='text-2xl'>Access Denied</CardTitle>
          <CardDescription className='text-base mt-2'>
            {message}
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {currentRole && (
            <div className='text-center'>
              <p className='text-sm text-muted-foreground'>
                Current role:{' '}
                <span className='font-medium text-foreground'>
                  {currentRole.replace('_', ' ')}
                </span>
              </p>
            </div>
          )}

          <div className='bg-muted/50 rounded-lg p-4 text-center'>
            <p className='text-sm text-muted-foreground'>
              This area is restricted to users with specific permissions. If you
              believe you should have access, please contact your administrator.
            </p>
          </div>

          <div className='flex flex-col sm:flex-row gap-2 pt-2'>
            <Button
              variant='outline'
              onClick={() => {
                void handleGoBack();
              }}
              className={hideHome ? 'w-full' : 'w-full sm:w-1/2'}
            >
              <ArrowLeft className='h-4 w-4 mr-2' />
              Go Back
            </Button>
            {!hideHome ? (
              <Button onClick={handleGoHome} className='w-full sm:w-1/2'>
                <Home className='h-4 w-4 mr-2' />
                Go to Home
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
